import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography, Box, Alert, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Chip, MenuItem, IconButton,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Layout from '../components/Layout';
import authService from '../services/authService';
import { useUnion } from '../contexts/UnionContext';
import axios from 'axios';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function BillingPeriods() {
  const { t } = useTranslation();
  const { activeUnion, isUnionAdmin } = useUnion();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New period dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newYear, setNewYear] = useState(new Date().getFullYear());
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);
  const [generating, setGenerating] = useState(false);

  // Preview state
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [previewBills, setPreviewBills] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Send state
  const [sending, setSending] = useState(null);

  const loadPeriods = useCallback(async () => {
    if (!activeUnion) return;
    try {
      setLoading(true);
      // Get sent invoices to build period summary
      const [invoicesRes, billsRes] = await Promise.all([
        axios.get('/api/sent-invoices/', { headers: authService.authHeader() }),
        axios.get('/api/bills/', { headers: authService.authHeader() }),
      ]);

      const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : invoicesRes.data.results || [];
      const bills = Array.isArray(billsRes.data) ? billsRes.data : billsRes.data.results || [];

      // Group by year/month
      const periodMap = {};

      bills.forEach(b => {
        const key = `${b.period_year}-${b.period_month}`;
        if (!periodMap[key]) {
          periodMap[key] = { year: b.period_year, month: b.period_month, bills: 0, sent: 0, paid: 0, draft: 0 };
        }
        periodMap[key].bills++;
        if (b.status === 'DRAFT') periodMap[key].draft++;
        if (b.status === 'SENT') periodMap[key].sent++;
        if (b.status === 'PAID') periodMap[key].paid++;
      });

      // Also count from sent invoices for periods that may not have bill records
      invoices.forEach(inv => {
        const key = `${inv.period_year}-${inv.period_month}`;
        if (!periodMap[key]) {
          periodMap[key] = { year: inv.period_year, month: inv.period_month, bills: 0, sent: 0, paid: 0, draft: 0 };
        }
        // sent invoices add to sent count (avoid double-counting with bills)
        if (!bills.some(b => b.period_year === inv.period_year && b.period_month === inv.period_month)) {
          periodMap[key].sent++;
          if (inv.paid_at) periodMap[key].paid++;
        }
      });

      const sorted = Object.values(periodMap).sort((a, b) =>
        b.year !== a.year ? b.year - a.year : b.month - a.month
      );
      setPeriods(sorted);
      setError('');
    } catch (err) {
      setError(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [activeUnion, t]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post('/api/bills/generate-period/', {
        year: newYear,
        month: newMonth,
      }, { headers: authService.authHeader() });
      setSuccess(t('billing.generated', { created: res.data.created, skipped: res.data.skipped }));
      setDialogOpen(false);
      loadPeriods();
    } catch (err) {
      setError(err.response?.data?.error || t('billing.failedGenerate'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async (year, month) => {
    const key = `${year}-${month}`;
    if (expandedPeriod === key) {
      setExpandedPeriod(null);
      return;
    }
    setExpandedPeriod(key);
    setPreviewLoading(true);
    try {
      const res = await axios.get('/api/bills/', {
        headers: authService.authHeader(),
        params: { period_year: year, period_month: month },
      });
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setPreviewBills(data);
    } catch {
      setPreviewBills([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendAll = async (year, month) => {
    if (!window.confirm(t('billing.sendConfirm', { month: MONTH_NAMES[month], year }))) return;
    const key = `${year}-${month}`;
    setSending(key);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post('/api/bills/send-period/', {
        year, month,
      }, { headers: authService.authHeader() });
      setSuccess(t('billing.sentResult', { queued: res.data.queued, skipped: res.data.skipped }) + (res.data.errors?.length ? ` Errors: ${res.data.errors.length}` : ''));
      loadPeriods();
    } catch (err) {
      setError(err.response?.data?.error || t('billing.failedSend'));
    } finally {
      setSending(null);
    }
  };

  if (!isUnionAdmin) {
    return (
      <Layout>
        <Alert severity="warning">{t('billing.noAdmin')}</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{t('billing.title')}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            {t('billing.newPeriod')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : periods.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('billing.noPeriods')}</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.period')}</TableCell>
                  <TableCell align="center">{t('billing.bills')}</TableCell>
                  <TableCell align="center">{t('common.status')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periods.map((p) => {
                  const key = `${p.year}-${p.month}`;
                  const isExpanded = expandedPeriod === key;
                  const isSending = sending === key;
                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {MONTH_NAMES[p.month]} {p.year}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{p.bills || p.sent}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {p.draft > 0 && <Chip label={t('billing.draft', { count: p.draft })} size="small" color="default" />}
                            {p.sent > 0 && <Chip label={t('billing.sent', { count: p.sent })} size="small" color="primary" />}
                            {p.paid > 0 && <Chip label={t('billing.paid', { count: p.paid })} size="small" color="success" />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handlePreview(p.year, p.month)} title={t('billing.previewBills')}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <Button
                            size="small"
                            startIcon={isSending ? <CircularProgress size={14} /> : <SendIcon />}
                            onClick={() => handleSendAll(p.year, p.month)}
                            disabled={!!isSending}
                            sx={{ ml: 1 }}
                          >
                            {t('billing.sendAll')}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2 }}>
                              {previewLoading ? (
                                <CircularProgress size={20} />
                              ) : previewBills.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">{t('billing.noBills')}</Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t('nav.apartment')}</TableCell>
                                      <TableCell align="right">{t('billing.total')}</TableCell>
                                      <TableCell>{t('common.status')}</TableCell>
                                      <TableCell>{t('billing.dueDate')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {previewBills.map((bill) => (
                                      <TableRow key={bill.id}>
                                        <TableCell>{bill.apartment_detail?.unit_number || bill.apartment}</TableCell>
                                        <TableCell align="right">{bill.total_amount} &euro;</TableCell>
                                        <TableCell>
                                          <Chip
                                            label={bill.status}
                                            size="small"
                                            color={bill.status === 'PAID' ? 'success' : bill.status === 'SENT' ? 'primary' : 'default'}
                                          />
                                        </TableCell>
                                        <TableCell>{bill.due_date}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* New Period Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('billing.generateTitle')}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <TextField
                label={t('apartments.year')}
                type="number"
                value={newYear}
                onChange={(e) => setNewYear(parseInt(e.target.value))}
                fullWidth
              />
              <TextField
                select
                label={t('apartments.month')}
                value={newMonth}
                onChange={(e) => setNewMonth(parseInt(e.target.value))}
                fullWidth
              >
                {MONTH_NAMES.slice(1).map((name, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('billing.generateDesc')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={generating}>{t('common.cancel')}</Button>
            <Button onClick={handleGenerate} variant="contained" disabled={generating}>
              {generating ? <CircularProgress size={20} /> : t('billing.generate')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default BillingPeriods;
